import moment from 'moment';

/**
 * @module google-trends-date-helper
 *
 * Google Trends Date Helper
 *
 * Business rules:
 *   - Data is pulled on the 1st and 14th of each month.
 *   - After a pull, there is a processing lag before the data is visible in the dashboard.
 *   - Data is displayed weekly: only Saturdays are valid selectable dates.
 *   - The most recent selectable Saturday is the last Saturday on or before the
 *     latest completed pull date (adjusted for lag).
 *
 * @param {number} processingLagDays – Days after a pull date before data becomes available (default 2).
 */

const PULL_DAYS_ASC = [1, 14]; // days of the month when data is pulled (ascending)
const PULL_DAYS_DESC = [14, 1]; // days of the month when data is pulled (descending)
export const DEFAULT_PROCESSING_LAG_DAYS = 2;

/**
 * Compute the most recent pull date whose data is actually available today,
 * accounting for the processing lag.
 *
 * Walk backwards through pull dates (this month, last month, ...) until we find
 * one where  pullDate + lag <= today.
 */
export const getLatestAvailablePullDate = (today, lagDays = DEFAULT_PROCESSING_LAG_DAYS) => {
    const now = today.clone().startOf('day');

    // Check pull dates going back up to 3 months (more than enough)
    for (let monthOffset = 0; monthOffset <= 3; monthOffset++) {
        const refMonth = now.clone().subtract(monthOffset, 'months');

        // Walk pull days in reverse (14, 1) so we find the most recent first
        for (const pullDay of PULL_DAYS_DESC) {
            // Guard against months with fewer days than pullDay (shouldn't happen with 1 & 14)
            const daysInMonth = refMonth.daysInMonth();
            if (pullDay > daysInMonth) continue;

            const pullDate = refMonth.clone().date(pullDay).startOf('day');
            const availableDate = pullDate.clone().add(lagDays, 'days');

            if (availableDate.isSameOrBefore(now)) {
                return pullDate;
            }
        }
    }

    throw new Error('Unable to determine the latest available pull date');
};

export const getNextPullDate = latestPullDate => {
    const pull = latestPullDate.clone();
    const pullDay = pull.date();

    // Find the next pull day in PULL_DAYS after the current one
    for (const day of PULL_DAYS_ASC) {
        if (day > pullDay) {
            return pull.clone().date(day).startOf('day');
        }
    }

    return pull.clone().add(1, 'month').date(PULL_DAYS_ASC[0]).startOf('day');
};

export const getMostRecentSaturday = date => {
    const d = date.clone().startOf('day');
    // moment day(): 0=Sun, 1=Mon, ... 6=Sat
    const dayOfWeek = d.day();
    const daysToSubtract = (dayOfWeek + 1) % 7; // Sat=0, Sun=1, Mon=2, ... Fri=6
    return d.subtract(daysToSubtract, 'days');
};

export const getLatestSelectableSaturday = (today, lagDays = DEFAULT_PROCESSING_LAG_DAYS) => {
    const latestPull = getLatestAvailablePullDate(today, lagDays);
    return getMostRecentSaturday(latestPull);
};

export const getDefaultDateRange = (lagDays = DEFAULT_PROCESSING_LAG_DAYS, defaultWeeksBack = 1) => {
    const today = moment();
    const endDate = getLatestSelectableSaturday(today, lagDays);
    const startDate = endDate.clone().subtract(defaultWeeksBack, 'weeks');
    return { startDate, endDate };
};

export const isDateDisabled = (date, lagDays = DEFAULT_PROCESSING_LAG_DAYS, todayOverride = null) => {
    const today = todayOverride || moment();
    const d = moment(date).startOf('day');

    // Rule 1: must be a Saturday
    if (d.day() !== 6) {
        return true;
    }

    // Rule 2: must not be in the future
    if (d.isAfter(today.clone().startOf('day'))) {
        return true;
    }

    // Rule 3: must be on or before the latest Saturday with available data
    const latestSaturday = getLatestSelectableSaturday(today, lagDays);
    if (d.isAfter(latestSaturday)) {
        return true;
    }

    return false;
};
